import React from 'react';
import { motion } from 'framer-motion';

interface HeroProps {
  title: string;
  description?: string;
  backgroundImage?: string;
  children?: React.ReactNode;
  compact?: boolean;
}

const Hero: React.FC<HeroProps> = ({ 
  title, 
  description, 
  backgroundImage, 
  children,
  compact = false 
}) => {
  const titleParts = title.split('Animatronics');
  const formattedTitle = titleParts.map((part, index) => (
    <React.Fragment key={index}>
      {part}
      {index < titleParts.length - 1 && <span className="text-brand">Animatronics</span>}
    </React.Fragment>
  ));

  return (
    <div className={`relative ${compact ? 'py-4 sm:py-12' : 'py-16 sm:py-24'} mb-6`}>
      {backgroundImage ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/90 to-background" />
        </>
      ) : (
        <div className="absolute inset-0 bg-background-secondary" />
      )}

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${compact ? 'mb-2 sm:mb-3' : 'mb-4 sm:mb-6'} text-content-primary`}
          >
            {formattedTitle}
          </motion.h1>
          {description && (
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className={`text-base sm:text-xl max-w-3xl mx-auto ${compact ? 'mb-2 sm:mb-4' : 'mb-6 sm:mb-8'} text-content-secondary`}
            >
              {description}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {children}
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;